import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useVipBadges } from '../../../hooks/useVipBadges';
import VipBadge from '../../../components/base/VipBadge';

interface Store {
  id: number;
  name: string;
  logo: string;
  description: string;
  category: string;
  subcategory?: string;
  rating: number;
  isActive: boolean;
  createdAt: string;
  promos: any[];
}

export default function PopularStores() {
  const navigate = useNavigate();
  const [stores, setStores] = useState<Store[]>([]);
  const { getVipBadge, isHighlighted } = useVipBadges();

  useEffect(() => {
    // Загружаем магазины из localStorage
    const savedStores = localStorage.getItem('admin_stores_v2') || localStorage.getItem('stores');
    if (savedStores) {
      const allStores = JSON.parse(savedStores);
      // Показываем только активные магазины с промокодами, отсортированные по количеству промокодов
      const activeStores = allStores
        .filter((store: Store) => store.isActive && store.promos && store.promos.length > 0)
        .sort((a: Store, b: Store) => {
          // VIP-магазины всегда первыми
          const aVip = getVipBadge(String(a.id)) ? 1 : 0;
          const bVip = getVipBadge(String(b.id)) ? 1 : 0;
          if (bVip !== aVip) return bVip - aVip;
          return b.promos.length - a.promos.length;
        })
        .slice(0, 6); // Показываем топ-6 магазинов
      setStores(activeStores);
    }
  }, []);

  const getStoreSlug = (storeName: string): string => {
    return storeName.toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9а-яё\-]/gi, '')
      .replace(/\.ru$/, '')
      .replace(/\.com$/, '');
  };

  const getMaxDiscount = (promos: any[]): string => {
    if (!promos || promos.length === 0) return '0%';
    
    const maxDiscount = Math.max(...promos.map(promo => {
      const match = promo.discount.match(/\d+/);
      return match ? parseInt(match[0]) : 0;
    }));
    
    return `${maxDiscount}%`;
  };

  if (stores.length === 0) {
    return null;
  }

  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Популярные магазины</h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Лучшие промокоды от проверенных интернет-магазинов
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {stores.map((store) => {
            const vip = getVipBadge(String(store.id));
            const highlighted = isHighlighted(String(store.id));
            return (
            <div
              key={store.id}
              onClick={() => navigate(`/store/${getStoreSlug(store.name)}`)}
              className={`bg-white rounded-xl p-6 shadow-sm hover:shadow-lg transition-all duration-300 cursor-pointer group border ${
                highlighted ? 'border-amber-300 ring-1 ring-amber-200 bg-amber-50/30' : 'border-gray-100'
              }`}
            >
              <div className="flex items-start space-x-4">
                <div className="w-16 h-12 flex items-center justify-center flex-shrink-0">
                  <img
                    src={store.logo}
                    alt={store.name}
                    className="max-w-full max-h-full object-contain rounded-lg"
                  />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <h3 className="font-bold text-gray-900 group-hover:text-blue-600 transition-colors">
                      {store.name}
                    </h3>
                    {vip && (
                      <VipBadge badge={vip.badge} badgeColor={vip.badgeColor} />
                    )}
                  </div>
                  <p className="text-gray-600 text-sm mb-3 leading-relaxed line-clamp-2">
                    {store.description}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">
                      {store.promos.length} промокодов
                    </span>
                    <span className="bg-orange-100 text-orange-600 px-2 py-1 rounded-full text-xs font-semibold">
                      до {getMaxDiscount(store.promos)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            );
          })}
        </div>

        <div className="text-center mt-8">
          <button
            onClick={() => navigate('/stores')}
            className="bg-blue-600 text-white px-8 py-3 rounded-full hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            Все магазины
          </button>
        </div>
      </div>
    </section>
  );
}
